/**
 * Deterministic identities.
 *
 * The engine needs UUIDv7 for `task_id`, `op_id`, and `device_id` — not
 * decoration. I3 makes identity client-minted so a task can be finished,
 * reworded, or re-entered *before the server has ever acknowledged it*, and so
 * an offline device can attribute the visits it opens to itself. `crypto.randomUUID`
 * is v4, which is still unique but not time-ordered, so the local outbox would
 * need a separate sort key and a v4 UUID cannot be recovered into a time.
 */

/** A v7 UUID: 48 bits of Unix milliseconds, then randomness. */
export function uuidv7(now: number = Date.now()): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);

  // 48-bit big-endian timestamp.
  const ms = BigInt(now);
  bytes[0] = Number((ms >> 40n) & 0xffn);
  bytes[1] = Number((ms >> 32n) & 0xffn);
  bytes[2] = Number((ms >> 24n) & 0xffn);
  bytes[3] = Number((ms >> 16n) & 0xffn);
  bytes[4] = Number((ms >> 8n) & 0xffn);
  bytes[5] = Number(ms & 0xffn);

  // Version 7 in the high nibble of byte 6, RFC 4122 variant in byte 8.
  bytes[6] = 0x70 | (bytes[6]! & 0x0f);
  bytes[8] = 0x80 | (bytes[8]! & 0x3f);

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32),
  ].join('-');
}

/** Whether a string parses as a UUID. */
export function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}